import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_MOST_LOGIN_TIME = `${API_BASE}/getMostLoginTime`;

function formatYMD(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

function getWeekNumber(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  let dayNum = d.getUTCDay();
  if (dayNum === 0) dayNum = 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

export default function MostLoginTimeChart({ type }) {
  const [dataPoints, setDataPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  async function loadData() {
    setLoading(true);
    setErr("");

    try {
      const now = new Date();
      const year = now.getFullYear();
      const params = { type, year };

      if (type === "daily") params.date = formatYMD(now);
      else if (type === "weekly") params.week = getWeekNumber(now);
      else if (type === "monthly") params.month = now.getMonth() + 1;

      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const res = await axios.get(ENDPOINT_MOST_LOGIN_TIME, {
        params,
        headers,
      });
      const rows = res.data?.data || [];

      const countsMap = new Map();

      // ---- DAILY ----
      if (type === "daily") {
        rows.forEach((r) =>
          countsMap.set(Number(r.hourly), Number(r.ctr || 0))
        );

        const base = Array.from({ length: 24 }, (_, h) => ({
          name: `${String(h).padStart(2, "0")}:00`,
          key: h,
        }));

        setDataPoints(
          base.map((b) => ({
            name: b.name,
            value: countsMap.get(b.key) || 0,
          }))
        );
      }

      // ---- WEEKLY ----
      else if (type === "weekly") {
        const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

        rows.forEach((r) => countsMap.set(Number(r.daily), Number(r.ctr || 0)));

        const base = [1, 2, 3, 4, 5, 6, 7].map((d) => ({
          key: d,
          name: dayNames[d],
        }));

        setDataPoints(
          base.map((b) => ({
            name: b.name,
            value: countsMap.get(b.key) || 0,
          }))
        );
      }

      // ---- MONTHLY ----
      else if (type === "monthly") {
        const monthNames = [
          "",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        rows.forEach((r) =>
          countsMap.set(Number(r.monthly), Number(r.ctr || 0))
        );

        const base = Array.from({ length: 12 }, (_, i) => ({
          key: i + 1,
          name: monthNames[i + 1],
        }));

        setDataPoints(
          base.map((b) => ({
            name: b.name,
            value: countsMap.get(b.key) || 0,
          }))
        );
      }
    } catch (e) {
      console.error(e);
      setErr("Error loading login time data.");
    } finally {
      setLoading(false);
    }
  }

  const axisLabel =
    type === "daily"
      ? "By Hour of Day"
      : type === "weekly"
      ? "By Day of Week"
      : "By Month";

  const strokeColor =
    type === "daily" ? "#3B82F6" : type === "weekly" ? "#A855F7" : "#22C55E";

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  if (err) return <div className="text-danger small">{err}</div>;

  return (
    <div style={{ width: "100%", height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={dataPoints}
          margin={{ top: 10, right: 10, left: 0, bottom: 25 }}
        >
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            angle={type === "daily" ? -45 : 0}
            textAnchor={type === "daily" ? "end" : "middle"}
            height={type === "daily" ? 45 : 30}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            label={{
              value: "Login Count",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11 },
            }}
            allowDecimals={false}
          />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke={strokeColor}
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <div
        className="text-center small text-muted"
        style={{ marginTop: "-20px" }}
      >
        {axisLabel}
      </div>
    </div>
  );
}
